<script setup>
import { ref, computed, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { useToggle } from '@vueuse/core';
import WidgetHead from './WidgetHead.vue';
import WidgetBody from './WidgetBody.vue';
import WidgetFooter from './WidgetFooter.vue';
import WidgetPreChatPreview from './WidgetPreChatPreview.vue';
import { useBranding } from 'shared/composables/useBranding';
import { useMapGetter } from 'dashboard/composables/store';
import Icon from 'dashboard/components-next/icon/Icon.vue';
import bubbleLogo from 'dashboard/assets/images/bubble-logo.svg';
import {
  getLauncherStyleById,
  isCustomLauncherStyle,
} from 'dashboard/constants/webWidgetLauncherStyles';
import { getWidgetSpacingPixels } from 'shared/constants/webWidgetSpacing';

const props = defineProps({
  welcomeHeading: {
    type: String,
    default: '',
  },
  welcomeTagline: {
    type: String,
    default: '',
  },
  websiteName: {
    type: String,
    required: true,
  },
  logo: {
    type: String,
    default: '',
  },
  isOnline: {
    type: Boolean,
    default: true,
  },
  color: {
    type: String,
    default: '',
  },
  fontColor: {
    type: String,
    default: '',
  },
  widgetBubblePosition: {
    type: String,
    default: '',
  },
  widgetVerticalSpacing: {
    type: String,
    default: 'standard',
  },
  widgetHorizontalSpacing: {
    type: String,
    default: 'standard',
  },
  widgetBubbleLauncherTitle: {
    type: String,
    default: '',
  },
  widgetBubbleType: {
    type: String,
    default: '',
  },
  widgetSize: {
    type: String,
    default: 'medium',
  },
  widgetDisplayOption: {
    type: String,
    default: 'mobile_and_desktop',
  },
  launcherIconStyle: {
    type: String,
    default: '1',
  },
  launcherCustomIconUrl: {
    type: String,
    default: '',
  },
  preChatFormEnabled: {
    type: Boolean,
    default: false,
  },
  preChatMessage: {
    type: String,
    default: '',
  },
  preChatFields: {
    type: Array,
    default: () => [],
  },
  selectedFeatureFlags: {
    type: Array,
    default: () => [],
  },
  chatInputText: {
    type: String,
    default: '',
  },
});

const { t } = useI18n();
const { replaceInstallationName } = useBranding();
const globalConfig = useMapGetter('globalConfig/get');

const [isWidgetVisible, toggleWidget] = useToggle(true);

const isPreChatSubmitted = ref(false);

const showPreChatPreview = computed(
  () =>
    props.preChatFormEnabled &&
    props.preChatFields.some(field => field.enabled) &&
    !isPreChatSubmitted.value
);

const showAttachmentInPreview = computed(() =>
  props.selectedFeatureFlags.includes('attachments')
);

const showEmojiPickerInPreview = computed(() =>
  props.selectedFeatureFlags.includes('emoji_picker')
);

watch(
  () => [props.preChatFormEnabled, props.preChatMessage, props.preChatFields],
  () => {
    isPreChatSubmitted.value = false;
  },
  { deep: true }
);

const getWidgetConfig = computed(() => ({
  welcomeHeading: props.welcomeHeading,
  welcomeTagline: props.welcomeTagline,
  welcomeHeadingPlaceholder: t(
    'INBOX_MGMT.WIDGET_CUSTOMIZATION_SECTIONS.HEADER_TITLE.PLACEHOLDER'
  ),
  welcomeTaglinePlaceholder: t(
    'INBOX_MGMT.WIDGET_CUSTOMIZATION_SECTIONS.HEADER_TAGLINE.PLACEHOLDER'
  ),
  websiteName: props.websiteName,
  logo: props.logo,
  isOnline: props.isOnline,
  color: props.color,
  fontColor: props.fontColor,
}));

const isCustomLauncher = computed(() =>
  isCustomLauncherStyle(props.launcherIconStyle)
);

const WIDGET_PREVIEW_SIZES = {
  small: {
    panelWidth: '260px',
    panelHeight: '420px',
    bubbleSize: '44px',
    launcherIconClass: 'size-5 shrink-0',
    expandedIconClass: 'size-5 shrink-0',
    expandedImageClass: 'size-5 shrink-0 rounded-full object-contain',
    closeIconClass: 'h-5',
  },
  medium: {
    panelWidth: '320px',
    panelHeight: '500px',
    bubbleSize: '56px',
    launcherIconClass: 'size-6 shrink-0',
    expandedIconClass: 'size-6 shrink-0',
    expandedImageClass: 'size-6 shrink-0 rounded-full object-contain',
    closeIconClass: 'h-6',
  },
  large: {
    panelWidth: '400px',
    panelHeight: '580px',
    bubbleSize: '68px',
    launcherIconClass: 'size-7 shrink-0',
    expandedIconClass: 'size-7 shrink-0',
    expandedImageClass: 'size-7 shrink-0 rounded-full object-contain',
    closeIconClass: 'h-7',
  },
};

const widgetSizeConfig = computed(
  () => WIDGET_PREVIEW_SIZES[props.widgetSize] || WIDGET_PREVIEW_SIZES.medium
);

const previewPanelStyle = computed(() => ({
  height: widgetSizeConfig.value.panelHeight,
}));

const previewContainerStyle = computed(() => {
  const bottom = getWidgetSpacingPixels(
    props.widgetVerticalSpacing,
    'vertical'
  );
  const horizontal = getWidgetSpacingPixels(
    props.widgetHorizontalSpacing,
    'horizontal'
  );

  return {
    width: widgetSizeConfig.value.panelWidth,
    bottom: `${bottom}px`,
    ...(props.widgetBubblePosition === 'left'
      ? { left: `${horizontal}px` }
      : { right: `${horizontal}px` }),
  };
});

const bubbleRowStyle = computed(() => ({
  justifyContent:
    props.widgetBubblePosition === 'left' ? 'flex-start' : 'flex-end',
}));

const isBubbleExpanded = computed(
  () => !isWidgetVisible.value && props.widgetBubbleType === 'expanded_bubble'
);

const getWidgetBubbleLauncherTitle = computed(() =>
  isWidgetVisible.value || props.widgetBubbleType === 'standard'
    ? ' '
    : props.widgetBubbleLauncherTitle
);

const presetLauncherStyle = computed(() =>
  getLauncherStyleById(props.launcherIconStyle)
);

const showPresetLauncherIcon = computed(
  () =>
    !isCustomLauncherStyle(props.launcherIconStyle) && presetLauncherStyle.value
);

const launcherCustomImage = computed(() => {
  if (!isCustomLauncherStyle(props.launcherIconStyle)) return '';
  return props.launcherCustomIconUrl || '';
});

const launcherBubbleImage = computed(
  () => launcherCustomImage.value || bubbleLogo
);

const launcherBubbleImageClass = computed(() => {
  const { expandedImageClass } = widgetSizeConfig.value;

  if (isCustomLauncher.value && launcherCustomImage.value) {
    if (isBubbleExpanded.value) {
      return `${expandedImageClass} rounded-full object-contain`;
    }
    return 'size-full object-contain p-1.5';
  }
  if (isBubbleExpanded.value) {
    return `${expandedImageClass} rounded-full object-cover`;
  }
  return 'size-full shrink-0 rounded-full object-cover object-center mx-auto';
});

const launcherButtonStyle = computed(() => {
  const baseStyle = {
    background: props.color,
    color: props.fontColor || '#ffffff',
  };

  const { bubbleSize } = widgetSizeConfig.value;

  if (isBubbleExpanded.value) {
    return {
      ...baseStyle,
      minHeight: bubbleSize,
      paddingLeft: '12px',
      paddingRight: '16px',
    };
  }

  return {
    ...baseStyle,
    width: bubbleSize,
    height: bubbleSize,
  };
});

const launcherButtonClass = computed(() => {
  const base = 'flex items-center justify-center rounded-full';

  if (isBubbleExpanded.value) {
    return `${base} gap-2.5 text-base font-medium`;
  }

  return base;
});

const presetLauncherIconClass = computed(() => {
  const { launcherIconClass, expandedIconClass } = widgetSizeConfig.value;
  return isBubbleExpanded.value ? expandedIconClass : launcherIconClass;
});

const closeIconLineClass = computed(
  () =>
    `absolute left-1/2 top-1/2 block w-0.5 -translate-x-1/2 -translate-y-1/2 ${widgetSizeConfig.value.closeIconClass}`
);

const handlePreChatSubmit = () => {
  isPreChatSubmitted.value = true;
};

const handleToggleWidget = () => {
  if (isWidgetVisible.value) {
    isPreChatSubmitted.value = false;
  }
  toggleWidget();
};
</script>

<template>
  <div class="flex h-full min-h-0 flex-1 flex-col">
    <div class="relative w-full min-h-[22rem] flex-1">
      <div class="absolute flex flex-col" :style="previewContainerStyle">
        <div
          v-if="isWidgetVisible"
          class="widget-wrapper flex w-full flex-shrink-0 flex-col justify-between rounded-lg shadow-md bg-n-slate-2 dark:bg-n-solid-1 mb-4"
          :style="previewPanelStyle"
          :data-display-option="widgetDisplayOption"
        >
          <WidgetHead :config="getWidgetConfig" />
          <div class="flex min-h-0 flex-1 flex-col">
            <template v-if="showPreChatPreview">
              <WidgetPreChatPreview
                :message="preChatMessage"
                :fields="preChatFields"
                :color="color"
                @submit="handlePreChatSubmit"
              />
              <div class="flex shrink-0 justify-center py-1">
                <a
                  class="items-center gap-0.5 text-n-slate-11 cursor-pointer flex filter grayscale opacity-90 hover:grayscale-0 hover:opacity-100 text-xxs"
                >
                  <img
                    class="max-w-2.5 max-h-2.5"
                    :src="globalConfig.logoThumbnail"
                  />
                  <span>
                    {{
                      replaceInstallationName(
                        $t('INBOX_MGMT.WIDGET_BUILDER.BRANDING_TEXT')
                      )
                    }}
                  </span>
                </a>
              </div>
            </template>
            <template v-else>
              <WidgetBody :config="getWidgetConfig" />
              <div class="mt-auto shrink-0">
                <WidgetFooter
                  :config="getWidgetConfig"
                  :show-attachment="showAttachmentInPreview"
                  :show-emoji-picker="showEmojiPickerInPreview"
                  :chat-input-text="chatInputText"
                />
                <div class="flex justify-center py-1">
                  <a
                    class="items-center gap-0.5 text-n-slate-11 cursor-pointer flex filter grayscale opacity-90 hover:grayscale-0 hover:opacity-100 text-xxs"
                  >
                    <img
                      class="max-w-2.5 max-h-2.5"
                      :src="globalConfig.logoThumbnail"
                    />
                    <span>
                      {{
                        replaceInstallationName(
                          $t('INBOX_MGMT.WIDGET_BUILDER.BRANDING_TEXT')
                        )
                      }}
                    </span>
                  </a>
                </div>
              </div>
            </template>
          </div>
        </div>

        <div class="flex w-full" :style="bubbleRowStyle">
          <button
            class="relative shrink-0 cursor-pointer overflow-hidden p-0"
            :class="launcherButtonClass"
            :style="launcherButtonStyle"
            @click="handleToggleWidget"
          >
            <Icon
              v-if="!isWidgetVisible && showPresetLauncherIcon"
              :icon="presetLauncherStyle.icon"
              class="shrink-0 [color:currentColor]"
              :class="[presetLauncherIconClass]"
            />
            <img
              v-else-if="!isWidgetVisible && launcherCustomImage"
              :src="launcherCustomImage"
              alt=""
              draggable="false"
              :class="launcherBubbleImageClass"
            />
            <img
              v-else-if="!isWidgetVisible"
              :src="launcherBubbleImage"
              alt=""
              draggable="false"
              :class="launcherBubbleImageClass"
            />
            <span
              v-if="isBubbleExpanded"
              class="whitespace-nowrap leading-none [color:currentColor]"
            >
              {{ getWidgetBubbleLauncherTitle }}
            </span>
            <div
              v-if="isWidgetVisible"
              class="pointer-events-none relative flex size-full items-center justify-center"
            >
              <span class="rotate-45 bg-white" :class="[closeIconLineClass]" />
              <span class="-rotate-45 bg-white" :class="[closeIconLineClass]" />
            </div>
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<style scoped lang="scss">
// Widget-specific color variables to match actual widget appearance
.widget-wrapper {
  // Light mode - widget colors
  --slate-1: 252 252 253;
  --slate-2: 249 249 251;
  --slate-3: 240 240 243;
  --slate-4: 232 232 236;
  --slate-5: 224 225 230;
  --slate-6: 217 217 224;
  --slate-7: 205 206 214;
  --slate-8: 185 187 198;
  --slate-9: 139 141 152;
  --slate-10: 128 131 141;
  --slate-11: 96 100 108;
  --slate-12: 28 32 36;

  --background-color: 253 253 253;
  --text-blue: 8 109 224;
  --border-container: 236 236 236;
  --border-strong: 235 235 235;
  --border-weak: 234 234 234;
  --solid-1: 255 255 255;
  --solid-2: 255 255 255;
  --solid-3: 255 255 255;
  --solid-active: 255 255 255;
  --solid-amber: 252 232 193;
  --solid-blue: 218 236 255;
  --solid-iris: 230 231 255;

  --alpha-1: 67, 67, 67, 0.06;
  --alpha-2: 201, 202, 207, 0.15;
  --alpha-3: 255, 255, 255, 0.96;
  --black-alpha-1: 0, 0, 0, 0.12;
  --black-alpha-2: 0, 0, 0, 0.04;
  --border-blue: 39, 129, 246, 0.5;
  --white-alpha: 255, 255, 255, 0.8;
}

// Dark mode - widget colors
.dark .widget-wrapper {
  --slate-1: 17 17 19;
  --slate-2: 24 25 27;
  --slate-3: 33 34 37;
  --slate-4: 39 42 45;
  --slate-5: 46 49 53;
  --slate-6: 54 58 63;
  --slate-7: 67 72 78;
  --slate-8: 90 97 105;
  --slate-9: 105 110 119;
  --slate-10: 119 123 132;
  --slate-11: 176 180 186;
  --slate-12: 237 238 240;

  --background-color: 14 14 16;
  --border-strong: 46 47 53;
  --border-weak: 34 35 40;
  --solid-1: 18 18 21;
  --solid-2: 24 25 29;
  --solid-3: 44 45 54;
  --solid-active: 53 57 66;
  --solid-amber: 42 37 30;
  --solid-blue: 16 49 91;
  --solid-iris: 38 42 101;
  --text-blue: 126 182 255;

  --alpha-1: 36, 36, 36, 0.8;
  --alpha-2: 139, 147, 182, 0.15;
  --alpha-3: 36, 38, 45, 0.9;
  --black-alpha-1: 0, 0, 0, 0.3;
  --black-alpha-2: 0, 0, 0, 0.2;
  --border-blue: 39, 129, 246, 0.5;
  --border-container: 236, 236, 236, 0;
  --white-alpha: 255, 255, 255, 0.1;
}
</style>
